import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import UIContext from './UIContext';
import AuthContext from './AuthContext';
import DataContext from './DataContext';
import ActionsContext from './ActionsContext';
import { useKimlik } from '../hooks/kimlik/useKimlik';
import { useCanliVeri } from '../hooks/veri/useCanliVeri';
import { clearStudentRecordsCache } from '../hooks/veri/useOgrenciDereceleri';
import { releaseSharedPaymentListeners } from '../services/payments';
import { useOgrenciIslemleri } from '../hooks/ogrenci/useOgrenciIslemleri';
import { useAppNavigation } from '../navigation/useAppNavigation';
import { STORAGE_KEYS } from '../utils/constants';
import { getCurrentPeriod } from '../utils/paymentPeriods';

const emptySubData = () => ({
  swimmingRecords: [], programs: [],
});

/**
 * Uygulama provider — SOLID ISP: UI / Auth / Data / Actions ayrı context'ler.
 * Navigasyon React Router üzerinden useAppNavigation ile yönetilir.
 */
export const AppProvider = ({ children }) => {
  const navigation = useAppNavigation();

  const [isDarkMode, setIsDarkMode] = useState(
    () => localStorage.getItem(STORAGE_KEYS.THEME) === 'dark'
  );
  const [showCookieBanner, setShowCookieBanner] = useState(
    () => localStorage.getItem(STORAGE_KEYS.COOKIE_CONSENT) === null
  );
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [showNotifications, setShowNotifications] = useState(false);
  const [visibleNotifsCount, setVisibleNotifsCount] = useState(10);
  const [notificationMsg, setNotificationMsg] = useState(null);
  const [activeModal, setActiveModal] = useState(null);
  const [selectedDayForProgram, setSelectedDayForProgram] = useState('Pazartesi');
  const [selectedDateISOForProgram, setSelectedDateISOForProgram] = useState(null);
  const [isAuthSubmitting, setIsAuthSubmitting] = useState(false);
  /** Ödeme takibi ve arşiv modalı aynı dönemi paylaşır */
  const [paymentPeriod, setPaymentPeriod] = useState(() => getCurrentPeriod());

  const [students, setStudents] = useState([]);
  const [instructors, setInstructors] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [selectedStudentSubData, setSelectedStudentSubData] = useState({
    ...emptySubData(), isLoading: false,
  });
  const [allStudentsPrograms, setAllStudentsPrograms] = useState({});
  const [mySubData, setMySubData] = useState({ ...emptySubData(), isLoading: false });
  const [liveDataReady, setLiveDataReady] = useState(false);
  const toastTimeoutRef = useRef(null);

  const showToast = useCallback((msg, type = 'success') => {
    if (toastTimeoutRef.current) {
      clearTimeout(toastTimeoutRef.current);
    }
    setNotificationMsg({ msg, type });
    toastTimeoutRef.current = setTimeout(() => {
      setNotificationMsg(null);
      toastTimeoutRef.current = null;
    }, 3500);
  }, []);

  useEffect(
    () => () => {
      if (toastTimeoutRef.current) {
        clearTimeout(toastTimeoutRef.current);
      }
    },
    []
  );

  const kimlik = useKimlik({
    showToast,
    navigation,
    setMySubData,
    setActiveModal,
  });

  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
      localStorage.setItem(STORAGE_KEYS.THEME, 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem(STORAGE_KEYS.THEME, 'light');
    }
  }, [isDarkMode]);

  useEffect(() => {
    if (!kimlik.loggedInUser) setLiveDataReady(false);
  }, [kimlik.loggedInUser]);

  // Ortak cihazda çıkış/hesap değişiminde önceki kullanıcının verisi ekranda kalmasın
  const authUid = kimlik.firebaseUser?.uid || null;
  const prevAuthUidRef = useRef(authUid);
  useEffect(() => {
    const prevUid = prevAuthUidRef.current;
    prevAuthUidRef.current = authUid;
    if (!prevUid || prevUid === authUid) return;
    setStudents([]);
    setInstructors([]);
    setNotifications([]);
    setAllStudentsPrograms({});
    setMySubData({ ...emptySubData(), isLoading: false });
    setSelectedStudentSubData({ ...emptySubData(), isLoading: false });
    setSelectedStudent(null);
    setActiveModal(null);
    setShowNotifications(false);
    setPaymentPeriod(getCurrentPeriod());
    clearStudentRecordsCache();
    releaseSharedPaymentListeners();
  }, [authUid]);

  useCanliVeri({
    firebaseUser: kimlik.firebaseUser,
    loggedInUser: kimlik.loggedInUser,
    selectedStudent,
    students,
    showToast,
    setInstructors,
    setStudents,
    setNotifications,
    setMySubData,
    setAllStudentsPrograms,
    setSelectedStudentSubData,
    setLoggedInUser: kimlik.setLoggedInUser,
    setLiveDataReady,
  });

  const ogrenciIslemleri = useOgrenciIslemleri({
    loggedInUser: kimlik.loggedInUser,
    setLoggedInUser: kimlik.setLoggedInUser,
    students,
    showToast,
  });

  const uiValue = useMemo(() => {
    const submitting = isAuthSubmitting;
    const setSubmitting = setIsAuthSubmitting;
    return {
      ...navigation,
      isDarkMode,
      setIsDarkMode,
      showCookieBanner,
      setShowCookieBanner,
      selectedStudent,
      setSelectedStudent,
      showNotifications,
      setShowNotifications,
      visibleNotifsCount,
      setVisibleNotifsCount,
      notificationMsg,
      activeModal,
      setActiveModal,
      selectedDayForProgram,
      setSelectedDayForProgram,
      selectedDateISOForProgram,
      setSelectedDateISOForProgram,
      paymentPeriod,
      setPaymentPeriod,
      isAuthSubmitting: submitting,
      setIsAuthSubmitting: setSubmitting,
      showToast,
    };
  }, [
      navigation,
      isDarkMode,
      showCookieBanner,
      selectedStudent,
      showNotifications,
      visibleNotifsCount,
      notificationMsg,
      activeModal,
      selectedDayForProgram,
      selectedDateISOForProgram,
      paymentPeriod,
      isAuthSubmitting,
      showToast,
    ]
  );

  const authValue = useMemo(
    () => ({
      firebaseUser: kimlik.firebaseUser,
      loggedInUser: kimlik.loggedInUser,
      setLoggedInUser: kimlik.setLoggedInUser,
      isLoading: kimlik.isLoading,
      handleLogout: kimlik.handleLogout,
      handleDeleteAccountPermanently: kimlik.handleDeleteAccountPermanently,
    }),
    [
      kimlik.firebaseUser,
      kimlik.loggedInUser,
      kimlik.isLoading,
      kimlik.setLoggedInUser,
      kimlik.handleLogout,
      kimlik.handleDeleteAccountPermanently,
    ]
  );

  const dataValue = useMemo(
    () => ({
      students,
      instructors,
      notifications,
      selectedStudentSubData,
      mySubData,
      allStudentsPrograms,
      liveDataReady,
    }),
    [
      students,
      instructors,
      notifications,
      selectedStudentSubData,
      mySubData,
      allStudentsPrograms,
      liveDataReady,
    ]
  );

  const actionsValue = useMemo(
    () => ({ ...ogrenciIslemleri }),
    [ogrenciIslemleri]
  );

  return (
    <UIContext.Provider value={uiValue}>
      <AuthContext.Provider value={authValue}>
        <DataContext.Provider value={dataValue}>
          <ActionsContext.Provider value={actionsValue}>
            {children}
          </ActionsContext.Provider>
        </DataContext.Provider>
      </AuthContext.Provider>
    </UIContext.Provider>
  );
};
